/**
 * LeaveApprovalBadgeSubscriber: keeps `useLeaveApprovalBadgesStore` in step with
 * the HR leave approval chain for the signed-in user.
 *
 * 1. Baseline: `LeaveApprovalService.getCapabilities()` for EVERY signed-in user
 *    (anyone can be a line approver). It runs on mount, on window focus, every
 *    5 minutes and on hub reconnect, through one react-query entry
 *    (`hrKeys.leaveApprovals.capabilities()`) that the approval pages share.
 * 2. Live: the `leaveapprovals` dashboard hub (`/hubs/dashboard/leaveapprovals`),
 *    connected only for line approvers, final approvers or users with pending
 *    items. Its `BadgeDelta` events carry SIGNED per-user changes
 *    (`myPendingApprovals`, `finalApprovalQueue`); `finalApprovalQueue` maps to
 *    the store's `finalQueue`.
 * 3. Each delta also invalidates the approval list and item queries, so open
 *    pages refresh and decided rows drop off. A short debounced capabilities
 *    refetch then re-syncs the absolute counts, which corrects any double count
 *    when the user's own action already refreshed the baseline.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import {
  useDomainDashboard,
  useLeaveApprovalService,
  type BadgeDeltaMessage,
  type LeaveApprovalBadgeDeltaMessage,
} from '@actschurch/ccms-sdk';
import { hrKeys } from '@/pages/app/HRManagement/utils/queryKeys';
import { useLeaveApprovalBadgesStore } from './leaveApprovalBadgesStore';

const FIVE_MINUTES = 5 * 60 * 1000;
const RESYNC_DELAY_MS = 2500;
const DOMAIN = 'leaveapprovals';
const DEBUG = import.meta.env.DEV;
const log = (...args: unknown[]) => {
  if (DEBUG) console.log('[LeaveApprovalBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

/** Every approval query except the capabilities entry (that one is re-synced on a timer). */
const isApprovalListOrItemQuery = (q: Query) => q.queryKey[2] !== 'capabilities';

export function LeaveApprovalBadgeSubscriber() {
  const service = useLeaveApprovalService();
  const queryClient = useQueryClient();

  const setBaseline = useLeaveApprovalBadgesStore((s) => s.setBaseline);
  const applyDelta = useLeaveApprovalBadgesStore((s) => s.applyDelta);
  const reset = useLeaveApprovalBadgesStore((s) => s.reset);
  const isLineApprover = useLeaveApprovalBadgesStore((s) => s.isLineApprover);
  const isFinalApprover = useLeaveApprovalBadgesStore((s) => s.isFinalApprover);
  const myPending = useLeaveApprovalBadgesStore((s) => s.myPendingApprovals);
  const myWaiting = useLeaveApprovalBadgesStore((s) => s.myWaitingOnEmployee);
  const finalQueue = useLeaveApprovalBadgesStore((s) => s.finalQueue);

  const { data, dataUpdatedAt, refetch } = useQuery({
    queryKey: hrKeys.leaveApprovals.capabilities(),
    queryFn: () => service.getCapabilities(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: FIVE_MINUTES,
    refetchIntervalInBackground: false,
  });

  // dataUpdatedAt is in the deps on purpose: a refetch that returns identical
  // counts keeps the same `data` reference, yet must still overwrite any drift
  // the deltas introduced.
  useEffect(() => {
    if (!data) return;
    log('baseline', data);
    setBaseline(data);
  }, [data, dataUpdatedAt, setBaseline]);

  const resyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleResync = useCallback(() => {
    if (resyncTimer.current) clearTimeout(resyncTimer.current);
    resyncTimer.current = setTimeout(() => {
      resyncTimer.current = null;
      void refetch();
    }, RESYNC_DELAY_MS);
  }, [refetch]);

  useEffect(
    () => () => {
      if (resyncTimer.current) clearTimeout(resyncTimer.current);
    },
    [],
  );

  const wsEnabled =
    isLineApprover === 1 || isFinalApprover === 1 || myPending > 0 || myWaiting > 0 || finalQueue > 0;

  const { isConnected } = useDomainDashboard(DOMAIN, {
    autoConnect: wsEnabled,
    autoSubscribe: false,
    onBadgeDelta: (raw: BadgeDeltaMessage) => {
      const msg = raw as unknown as LeaveApprovalBadgeDeltaMessage;
      if ((msg.domain ?? '').toLowerCase() !== DOMAIN) return;
      log('delta', msg.reason, msg.refId, msg.deltas);
      applyDelta({
        myPendingApprovals: msg.deltas?.myPendingApprovals ?? 0,
        finalQueue: msg.deltas?.finalApprovalQueue ?? 0,
      });
      void queryClient.invalidateQueries({
        queryKey: hrKeys.leaveApprovals.all(),
        predicate: isApprovalListOrItemQuery,
      });
      if (msg.refId) {
        void queryClient.invalidateQueries({ queryKey: hrKeys.leave(msg.refId) });
      }
      scheduleResync();
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!wsEnabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected, refetching capabilities');
      void refetch();
    }
  }, [wsEnabled, isConnected, refetch]);

  useEffect(() => () => reset(), [reset]);

  return null;
}
